import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, MapPin, Truck, CheckCircle2, CalendarDays, Shield } from "lucide-react";
import { heroFeatures, images } from "../mock";
import { Reveal } from "../hooks/useReveal";
import { useBooking } from "../context/BookingContext";

const iconMap = { MapPin, Truck, Star };

const Hero = () => {
  const booking = useBooking();
  return (
    <section className="relative bg-white overflow-hidden">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="max-w-[1280px] mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 text-[13px] text-amber-900 mb-6">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              4.9★ op Google — 43+ tevreden klanten
            </div>
            <h1 className="text-[38px] md:text-5xl lg:text-[58px] font-bold leading-[1.05] tracking-tight text-neutral-900 mb-6">
              Altijd een
              <br />
              <span className="italic font-light">werkende</span> computer.
              <br className="hidden sm:block" />
              <span className="text-neutral-400">IT Support vanaf €15/mnd — Beverwijk en omgeving.</span>
            </h1>
            <p className="text-lg text-neutral-600 leading-relaxed mb-8 max-w-xl">
              Vaste hulp, virusbeveiliging en onderhoud vanaf <strong>€15/mnd</strong> — voor thuis of op kantoor.
              Ook voor losse reparaties staan wij voor u klaar.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Link
                to="/it-support"
                className="group inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-black text-white px-7 py-4 text-[15px] font-medium tracking-wide transition-colors"
                data-testid="hero-cta-it-support"
              >
                <Shield size={18} />
                Bekijk IT Support
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={booking.open}
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-neutral-300 hover:border-neutral-900 text-neutral-700 hover:text-neutral-900 px-6 py-3.5 text-sm font-medium tracking-wide transition-colors"
                data-testid="hero-cta-book"
              >
                <CalendarDays size={16} />
                Maak afspraak
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-100 rounded-full opacity-50 blur-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-sky-100 rounded-full opacity-50 blur-2xl"></div>
            <div className="relative rounded-lg overflow-hidden border border-neutral-200 shadow-xl">
              <img
                src={images.hero}
                alt="IT Support Computheer"
                loading="eager"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">
                    Direct geholpen
                  </p>
                  <p className="text-lg font-semibold text-neutral-900">Binnen 24 uur</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Service</p>
                  <p className="text-lg font-semibold text-neutral-900">Aan huis</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
          {heroFeatures.map((feature, idx) => {
            const Icon = iconMap[feature.icon];
            return (
              <Reveal
                key={idx}
                delay={idx * 120}
                className="group bg-white border border-neutral-200 p-8 hover:border-neutral-900 transition-colors duration-300 hover:shadow-lg"
              >
                <div className="w-14 h-14 rounded-full border-2 border-neutral-900 flex items-center justify-center mb-6 group-hover:bg-neutral-900 transition-colors">
                  <Icon
                    size={22}
                    className="text-neutral-900 group-hover:text-white transition-colors"
                  />
                </div>
                <h3 className="text-base font-bold tracking-wider text-neutral-900 mb-3 uppercase">
                  {feature.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-[15px]">{feature.description}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
